import { Component } from 'react';
import type { ReactNode, ErrorInfo } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  state: State = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('ErrorBoundary caught:', error, info.componentStack);
  }

  render() {
    if (this.state.hasError) {
      return this.props.fallback ?? (
        <div className="card border-metric-negative/30 m-4" style={{ borderLeft: '4px solid var(--color-metric-negative)' }}>
          <h3 className="text-sm font-semibold text-metric-negative mb-2">Something went wrong</h3>
          <p className="text-xs text-secondary mb-3">
            An error occurred while rendering this section. Try refreshing the page or adjusting your configuration.
          </p>
          <details className="text-xs text-muted">
            <summary className="cursor-pointer hover:text-secondary">Error details</summary>
            <pre className="mt-2 p-2 rounded text-micro font-mono overflow-x-auto" style={{ backgroundColor: 'var(--color-surface)' }}>
              {this.state.error?.message}
            </pre>
          </details>
          <button
            onClick={() => this.setState({ hasError: false, error: null })}
            className="mt-3 px-3 py-1.5 text-xs font-medium rounded bg-chart-teal/10 text-chart-teal hover:bg-chart-teal/20"
          >
            Try Again
          </button>
        </div>
      );
    }
    return this.props.children;
  }
}
